import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QuickExpenseForm from './QuickExpenseForm.jsx'
import SubmitSpotForm from './SubmitSpotForm.jsx'
import licenseManager from '../utils/licenseManager.js'

function QuickMenu({ isOpen, onClose, onNavigate, onGiftPremium, userData }) {
  const { t } = useTranslation()
  const [showExpenseForm, setShowExpenseForm] = useState(false)
  const [showSpotForm, setShowSpotForm] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [isPremium, setIsPremium] = useState(false)

  // Resolve premium status when the sheet opens (drives the gift/get-premium action)
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    licenseManager.hasFeatureAccess('multiple_trips').then(p => { if (!cancelled) setIsPremium(p) })
    return () => { cancelled = true }
  }, [isOpen])

  if (!isOpen && !isClosing) return null

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      setIsClosing(false)
      setShowExpenseForm(false)
      setShowSpotForm(false)
      onClose()
    }, 300)
  }

  const handleAction = (view, options = {}) => {
    onNavigate(view, options)
    setShowExpenseForm(false)
    setShowSpotForm(false)
    setIsClosing(false)
    onClose()
  }

  const actions = [
    {
      icon: '📝',
      label: t('quickMenu.diary'),
      action: () => handleAction('notes', { directToWriteMemory: true })
    },
    {
      icon: '🏙️',
      label: t('quickMenu.citySuggestions'),
      action: () => handleAction('plans')
    },
    {
      icon: '📖',
      label: t('menu.atlas'),
      action: () => handleAction('atlas')
    },
    {
      icon: '🌍',
      label: t('quickMenu.explore'),
      action: () => handleAction('explore')
    },
    {
      icon: '💰',
      label: t('quickMenu.logExpense'),
      action: () => setShowExpenseForm(true)
    },
    {
      icon: '🚨',
      label: t('quickMenu.emergencyInfo'),
      action: () => handleAction('info')
    },
    {
      icon: '✅',
      label: t('quickMenu.checklists'),
      action: () => handleAction('checklists')
    },
    {
      icon: '📕',
      label: t('quickMenu.summary'),
      action: () => handleAction('summary')
    },
    {
      icon: '📍',
      label: t('quickMenu.submitSpot'),
      action: () => setShowSpotForm(true)
    },
    {
      icon: isPremium ? '🎁' : '⭐',
      label: isPremium ? t('quickMenu.giftPremium') : t('quickMenu.getPremium'),
      action: () => { handleClose(); setTimeout(() => onGiftPremium?.(), 320) }
    },
    {
      icon: '📱',
      label: t('quickMenu.openFullApp'),
      action: handleClose
    }
  ]

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10001,
          animation: isClosing ? 'fadeOut 0.3s ease forwards' : 'fadeIn 0.3s ease forwards'
        }}
      />

      {/* Bottom Sheet */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderRadius: '24px 24px 0 0',
        zIndex: 10002,
        padding: '12px 20px 30px 20px',
        maxHeight: '80vh',
        overflowY: 'auto',
        animation: isClosing ? 'slideDownOut 0.3s ease forwards' : 'slideUpIn 0.3s ease forwards',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Handle Bar */}
        <div style={{
          width: '40px',
          height: '4px',
          background: '#d5cfc5',
          borderRadius: '2px',
          margin: '0 auto 16px auto'
        }} />

        {/* Title */}
        <h2 style={{
          fontSize: '20px',
          fontWeight: '800',
          color: '#8B7355',
          margin: '0 0 16px 0',
          textAlign: 'center',
          letterSpacing: '-0.5px'
        }}>{t('quickMenu.title')}</h2>

        {showExpenseForm ? (
          <div>
            <button
              onClick={() => setShowExpenseForm(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#B8A082',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '8px',
                padding: '4px 0'
              }}
            >
              ← {t('navigation.back')}
            </button>
            <QuickExpenseForm
              onClose={handleClose}
              onSaved={() => {}}
            />
          </div>
        ) : showSpotForm ? (
          <div>
            <button
              onClick={() => setShowSpotForm(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#B8A082',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '8px',
                padding: '4px 0'
              }}
            >
              ← {t('navigation.back')}
            </button>
            <SubmitSpotForm
              userData={userData}
              onClose={handleClose}
              onSubmitted={() => {}}
            />
          </div>
        ) : (
          /* Action Buttons Grid */
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px'
          }}>
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  background: '#f8f6f0',
                  border: '2px solid #e8e0d4',
                  borderRadius: '16px',
                  padding: '16px 8px',
                  minHeight: '80px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{ fontSize: '28px' }}>{action.icon}</span>
                <span style={{
                  fontSize: '13px',
                  fontWeight: '700',
                  color: '#8B7355',
                  textAlign: 'center',
                  lineHeight: '1.2'
                }}>{action.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default QuickMenu
